<script lang="ts" setup>
import type {Area} from "#magnolia-layer/types/magnolia-types";
import {type Delivery, isLinkTypeExternal} from "#magnolia-layer/types/api";
import type {Author} from "~/types/mgnl";
import usePageQueryRef from "~/composables/usePageQueryRef";
import {useOffsetPagination} from "@vueuse/core";
import ErrorDisplayConcise from "~/components/ErrorDisplayConcise.vue";

const props = defineProps<{
  metadata: Area;
  authorsRootLink: string;
}>();

const pageRef = usePageQueryRef();
const limit = ref(8);
const offset = computed(() => {
  return (pageRef.value - 1) * limit.value;
});

const runtimeConfig = useRuntimeConfig();
const context = useMagnoliaContext();
const {data: content, error} = await useFetch<Delivery<Author>>(
  "/api/paginatedChildPages",
  {
    params: {
      deliveryApi: runtimeConfig.public.mgnl.apiAuthors,
      offset: offset,
      limit: limit,
      path: props.authorsRootLink,
      lang: context.searchParams?.lang,
    },
    watch: [offset],
  },
);

const offsetPagination = useOffsetPagination({
  page: pageRef,
  total: content.value?.total,
  pageSize: limit.value,
});
usePaginationHeadLinks(offsetPagination);
useErrorStatusForwarding(error);

const getLink = (author: Author) => {
  return resolveLink(author);
};
</script>

<template>
  <section class="col-span-full content-grid py-22">
    <h1 class="sr-only">{{ $t("label.overview") }}</h1>
    <ErrorDisplayConcise v-if="error" :error></ErrorDisplayConcise>
    <div v-else class="articles grid grid-cols-12 gap-3 items-end">
      <template v-for="page in content?.results" :key="page['@id']">
        <TextMediaLayout
          :compact="false"
          :border="false"
          :link="getLink(page)"
          :linkText="
            isLinkTypeExternal(page.link)
              ? $t('label.visitWebsite')
              : $t('label.viewContributions')
          "
          :category="undefined"
          :title="page.authorName"
          :role="page.authorTitle">
          <template #media>
            <MagnoliaPicture
              :asset="page.authorImage"
              :modifiers="{
                extract: imageToExtractionArea(page.authorImage, 1),
                grayscale: 1,
              }"
              loading="lazy"
              class="max-h-80 h-full w-full *:w-full *:h-full *:object-contain *:object-center"
              sizes="400 md:600 lg:520 xl:640"
              v-if="page.authorImage">
            </MagnoliaPicture>
          </template>
          <template v-if="page.authorDescription">{{
            page.authorDescription
          }}</template>
        </TextMediaLayout>
      </template>
    </div>
    <Pagination
      v-if="offsetPagination.pageCount.value > 1"
      :enable-pages="false"
      :enable-links="true"
      :pagination="offsetPagination"
      :total="content?.total ?? 0"></Pagination>
  </section>
</template>

<style lang="scss" scoped></style>
